import React, {useState, createRef, useCallback, useContext} from 'react';
import { useQueryClient } from 'react-query';
import MaterialTable from "@material-table/core";
import { TablePagination, useTheme } from "@material-ui/core";
import moment from 'moment';
import axios from "axios";
import __ from "lodash";

import {defaultComponents} from "../../../controls/Material-Table-Components";
import { MaskedNumber, ToggleEdit, DatePicker, CurrencyValue, ConvertedNumber, ComboSelect } from "../../../controls";
import AirportLookup from "../../Airports/components/AirportsLookup";
import UnitPrice from "./UnitPrice";
import FuelMarketsLookup from "./FuelMarketsLookup";

import { useAddFuelRate, useUpdateFuelRate, useDeleteFuelRate, useMonthlyFuelRates } from "../../../services/fuel";

import FeeTooltip from "./FeeTooltip";
import FuelDetails from "./FuelDetails.js";
import FuelRatesValidator from "../validators/FuelRatesValidator";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import FuelRateDuplicateDialog from "./FuelRateDuplicateDialog";
import ConfirmFuelDialog from "./ConfirmFuelDialog";

import SystemContext from "../../../SystemContext";
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import AirportFuelSupplierCodeLookup from '../../Companies/components/AirportFuelSupplierCodeLookup';
const apiSitePrefix = getApiSitePrefix();

const FuelRates = ({ month, year, canEdit, canViewDetails}) => {
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const queryClient = useQueryClient();
  const tableRef = createRef();

  const handleAddFuelRate = useAddFuelRate();
  const handleUpdateFuelRate = useUpdateFuelRate();
  const handleDeleteFuelRate = useDeleteFuelRate();

  const [state] = useState({
    canEdit: canEdit,
    canViewDetails: canViewDetails
  });

  const [fuelRateDuplicateDialogProps, setFuelRateDuplicateDialogProps] = useState({
    open: false,
    uuids: []
  });

  const [confirmFuelDialogProps, setConfirmFuelDialogProps] = useState({
    open: false,
    record: null
  });

  const rowsPerPage = 10;

  const extraKeys = [year, month];
  const monthlyFuelData = useMonthlyFuelRates({ tableRef, queryClient, extraKeys });
  
  const dateFormat = "DD/MM/YYYY";
  const today = moment().subtract(3,'months').toISOString();

  async function downloadFuelRates(year, month, summaryRates) {

    const url = summaryRates ? `${apiSitePrefix}/fuelRates/exportMcsFormat/${year}/${month}`
                             : `${apiSitePrefix}/fuelRates/exportToExcel/${year}/${month}`;

    const response = await axios.get(url, {
      headers: {
        //'Access-Control-Allow-Headers': ['Origin', 'X-Requested-With', 'Content-Type', 'Accept'],
        'Access-Control-Allow-Origin': '*',
      }
    });

    if (response && response.data) {
      if (response.data.status) {
        const fileName = summaryRates ? `Fuel Cost Data - ${year}${month}.xlsx`
                                      : `Magma Contracted Fuel Prices ${year}${month}.xlsx`;
        let a = document.createElement('a');
        a.href = response.data.signedUrl;
        a.download = fileName;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
        }, 0);
      } else {
        SystemData.openAlert(
          "Download Error",
          "The Fuel Rates Excel file could not be downloaded due to server error: " + response.data.error
        );
      }
    }
  }

  const handleConfirm = (e, rowData) => {
    setConfirmFuelDialogProps({
      ...confirmFuelDialogProps,
      record: rowData,
      open: true,
      onChange: (event) => {
        if (event.state === "cancel") {
          setConfirmFuelDialogProps ({
            ...confirmFuelDialogProps,
            open: false,
          });
        }
        if (event.state === "confirm") {
          handleUpdateFuelRate.mutate({record: {...rowData, verifiedOn: moment.utc().toDate()}, oldRecord: rowData, extraKeys });

          setConfirmFuelDialogProps ({
            ...confirmFuelDialogProps,
            open: false,
          });
        }
      }
    })
  }

  const handleDialogChange = (event) => {
    if (event.state === "cancel") {
      setFuelRateDuplicateDialogProps ({
        ...fuelRateDuplicateDialogProps,
        open: false,
      });
    }
    if (event.state === "save") {
      axios.put(`${apiSitePrefix}/fuelrates/duplicate/${event.record.validFrom}/${event.record.validTo}/${event.record.uuids.join(":")}`)
      .then(() => {
        queryClient.invalidateQueries(['FuelRates',...extraKeys]);
        setFuelRateDuplicateDialogProps ({
          ...fuelRateDuplicateDialogProps,
          open: false,
        });  
      })
      .catch(err => {
        if (err.response && err.response.data && err.response.data.error) {
          SystemData.openAlert('Save Error', err.errorMessage);
        } else {
          SystemData.openAlert('Unexpected Error', 'An unexpected error occurred duplicating the rate line');
        }
      });
    }
  }
  
  const handleDuplicate = (e, rowData) => {
    setFuelRateDuplicateDialogProps({
      ...fuelRateDuplicateDialogProps,
      uuids: [rowData.fuelRateUuid],
      validFrom: rowData.validFrom,
      validTo: rowData.validTo,
      open: true,
      onChange: handleDialogChange
    })
  }

  const handleDuplicateSelection = (e, rows) => {
    setFuelRateDuplicateDialogProps({
      ...fuelRateDuplicateDialogProps,
      uuids: rows.map(row=>row.fuelRateUuid),
      validFrom: rows[0].validFrom,
      validTo: rows[0].validTo,
      open: true,
      onChange: handleDialogChange
    })
  }

  const handleChange = async (field, rowData, onRowDataChange, value, onChange) => {
    let fuelMarketRate = null;
    const newRowData = {...rowData};      
    newRowData[field] = value;

    try {
      fuelMarketRate = await axios.get(`${apiSitePrefix}/fuelmarketrates/get/${newRowData.priceBasis}/${moment(newRowData.validFrom).format('YYYY-MM-DD')}`);
    } catch(err) {}
    if (fuelMarketRate && fuelMarketRate.data  && !fuelMarketRate.data.error) {
      const patch = __.omit(fuelMarketRate.data, ['validFrom', 'rateType','fuelMarketRateUuid', 'fuelMarketUuid']);
      onRowDataChange({...newRowData, ...patch});
    } else {
      onChange(value);
    }
  }

  const validatorColumn = ValidatorColumn(FuelRatesValidator, true);

  const getFuelColumns = useCallback(
    () => {
      return [
        {...validatorColumn},
        {
          title: "Airport",
          field: "airportCode",
          editable: state.canEdit ? "onAdd" : "never",
          ...(ColumnWidth(70)),
          cellStyle: state.canEdit || state.canViewDetails?{ width: 66, minWidth: 66, maxWidth: 66, padding: "8px 2px" }:{ width: 66, minWidth: 66, maxWidth: 66, padding: "8px 12px" },
          headerStyle: state.canEdit || state.canViewDetails?{padding: "8px 2px" }:{padding: "8px 12px" },
          editComponent: (props) => (
            <AirportLookup
              value={props.value}
              onChange={props.onChange}
              style= {{ fontSize: "0.875rem" }}
            />
          ),
        },
        {
          title: "Variant",
          field: "airportVariant",
          sorting: false,
          editable: state.canEdit ? "onAdd" : "never",
          ...(ColumnWidth(70)),
          emptyValue: "",
          initialEditValue: "",
          // lookup: {
          //   NONE: "",
          //   CIV: "(civ)",
          //   DOM: "(dom)",
          //   MIL: "(mil)",
          // },
          render: rowData => (
            <ComboSelect
              group="AIRPORT VARIANTS"
              subGroup=""
              value={rowData.airportVariant}
              displayOnly={true}
            />
          ),
          editComponent: (props) => {
            return (
              <ComboSelect
                group="AIRPORT VARIANTS"
                subGroup=""
                value={props.value}
                onChange={props.onChange}
              />
            )
          }
        },
        {
          title: "Supplier",
          field: "supplierCode",
          editable: state.canEdit ? "onAdd" : "never",
          ...(ColumnWidth(120)),
          emptyValue: "",
          initialEditValue: "",
          editComponent: (props) => (
            <AirportFuelSupplierCodeLookup
              returnType="object"
              value={props.value}
              airportCode={props.rowData.airportCode}
              onChange={(obj) => {
                if (obj.data) {
                  let data = { ...props.rowData, supplierCode: obj.value, exchangeRateType: obj.data.exchangeRateType||'SYSTEM', currencyCode: props.rowData.currencyCode||obj.data.defaultCurrencyCode, priceUom: props.rowData.priceUom||'USG' };
                  props.onRowDataChange(data);                  
                } else {
                  props.onChange(obj.value);
                }              
              }}
            />
          ),
        },
        {
          ...(ColumnWidth(150)),
          title: "Contract Type",
          field: "contractType",
          editable: "never",
          hidden: state.canEdit,
          cellStyle: { padding: "8px 2px"},
          headerStyle: { padding: "8px 2px" },
          render: rowData => (
            <div>{__.startCase((rowData.contractType||"").toLowerCase())}</div>
          )
        },
        {
          title: "Price Period",
          field: "pricePeriod",
          editable: "never",
          hidden: state.canEdit,
          cellStyle: { padding: "8px 2px"},
          headerStyle: { padding: "8px 2px" },
        },
        {
          title: "Price Basis",
          field: "priceBasis",
          editable: state.canEdit ? "onAdd" : "never",
          hidden: !state.canEdit,
          cellStyle: { padding: "8px 2px"},
          headerStyle: { padding: "8px 2px" },
          emptyValue: "",
          initialEditValue: "",
          editComponent: (props) => (
            <FuelMarketsLookup
              value={props.value}
              onChange={
                (value) => {
                  handleChange('priceBasis', props.rowData, props.onRowDataChange, value, props.onChange);
                }
              }
              style= {{ fontSize: "0.875rem" }}
              allowEmpty={true}
            />
          )
        },
        {
          title: "Valid From",
          field: "validFrom",
          editable: state.canEdit ? "onAdd" : "never",
          ...(ColumnWidth(120)),
          type: "date",
          format: "dd MMM",
          initialEditValue: moment().toISOString(),
          editComponent: (props) => (
            <DatePicker
              minDate={today}
              value={props.value}
              disableToolbar={true}
              autoOk={true}
              format={dateFormat}
              onChange={
                (value) => handleChange('validFrom', props.rowData, props.onRowDataChange, value, props.onChange)
              }
            />
          ),
          render: rowData => moment.utc(rowData.validFrom).format(dateFormat)
        },
        {
          title: "Valid To",
          field: "validTo",
          editable: state.canEdit ? "onAdd" : "never",
          ...(ColumnWidth(120)),
          type: "date",
          format: "dd MMM",
          initialEditValue: moment().toISOString(),
          editComponent: (props) => (
            <DatePicker
              minDate={today}
              value={props.value}
              disableToolbar={true}
              autoOk={true}
              format={dateFormat}
              onChange={props.onChange}
            />
          ),
          render: rowData => moment.utc(rowData.validTo).format(dateFormat)
        },
        {
          title: (<span>Euro Rate</span>),
          field: "plattsPrice",
          sorting: false,
          type: "numeric",
          editable: "never",
          hidden: !state.canViewDetails,
          emptyValue: 0,
          initialEditValue: 0,
          ...(ColumnWidth(95)),
          render: (rowData, rowType, updatedData) => {
            if (!rowData.priceUom || !`${rowData.currencyCode}`.startsWith('EUR') || rowData.exchangeRateType === 'OTHER' || __.isEmpty(rowData.exchangeRateType)) return (<></>);
            return (
              <ConvertedNumber
                displayType="text"
                value={rowData.rate}
                convertedValue={rowData.plattsPrice}
                showError={false}
                decimalScale={5}
                rateProviderCode={rowData.exchangeRateType}
                fromCurrencyCode={"USDC"}
                toCurrencyCode="EUR"
                rateDate={rowData.validFrom}
                prefix="€"
                fixedDecimalScale={true}
                uom={"USG"}
                toUom={"USG"}
                style={{ width:"100%", fontSize: "0.875rem", textAlign: "right"}}
              />
              // <ConvertedNumber
              //   displayType="text"
              //   value={rowData.plattsPrice}
              //   convertedValue={rowData.plattsPrice}
              //   showError={false}
              //   decimalScale={5}
              //   rateProviderCode={rowData.exchangeRateType}
              //   fromCurrencyCode={rowData.currencyCode}
              //   toCurrencyCode="EUR"
              //   rateDate={rowData.validFrom}
              //   prefix="€"
              //   fixedDecimalScale={true}
              //   uom={rowData.priceUom}
              //   toUom={"USG"}
              //   style={{ width:"100%", fontSize: "0.875rem", textAlign: "right"}}
              // />
            )
          },
        },        
        {
          title: "Platts Price",
          field: "plattsPrice",
          sorting: false,
          type: "numeric",
          editable: ((cell,rowData,updatedData) => updatedData.priceBasis===""?state.canEdit:false),
          hidden: !state.canEdit,
          ...(ColumnWidth(200)),
          render: (rowData, rowType, updatedData) => (
            <div style={{ width: 172, float: "right"}}>
              <CurrencyValue
                decimalScale={3}
                fixedDecimalScale={true}
                currencyCode={updatedData.currencyCode}
                value={updatedData.plattsPrice}
              />&nbsp;{updatedData.priceUom?`/ ${updatedData.priceUom}`: ""}
            </div>
          ),
          editComponent: (props) => (
            <UnitPrice
              currencyCode={props.rowData.currencyCode}
              uom={props.rowData.priceUom}
              amount={props.value}
              defaultCurrencyCode="USDC"
              conversionCurrencyCode="USDC"
              conversionRateProviderCode={props.rowData.exchangeRateType}
              convertedValue={props.rowData.dollarPlattsPrice}
              conversionDate={props.rowData.validFrom}
              defaultUom="USG"
              currencyStyle={{fontSize: "0.875rem"}}
              uomStyle={{fontSize: "0.875rem"}}
              onChange={e => {
                let data = { ...props.rowData, plattsPrice: e.amount, priceUom: e.uom, currencyCode: e.currencyCode, usgConversionFactor: e.usgConversionFactor };//, dollarPlattsPrice: e.convertedValue
                props.onRowDataChange(data);
              }}
            />
          ),  
        },
        {
          title: (<span>Platts Price<br/>($c/AG)</span>),//"Platts Price ($c/AG)",
          field: "dollarPlattsPrice",
          sorting: false,
          type: "numeric",
          hidden: !state.canEdit,
          ...(ColumnWidth(120)),
          emptyValue: 0,
          initialEditValue: 0,
          editable: ((cell,rowData,updatedData) => updatedData.priceBasis===""?state.canEdit:false),
          editComponent: (props) => (
            <ToggleEdit
              canEdit={props.rowData.overrideCalculation}
              value={props.rowData.dollarPlattsPrice}
              inputProps={{
                conversionValue: props.rowData.plattsPrice,
                rateDate: props.rowData.validFrom,
                uom:props.rowData.priceUom,
                currencyCode: props.rowData.currencyCode,
                onChange: props.onChange,
                onRenderChange: (convertedValue) => {
                  if (!isNaN(convertedValue)) {                    
                    let data = { ...props.rowData, dollarPlattsPrice: convertedValue };
                    props.onRowDataChange(data);
                  }  
                }
              }}
//              defaultValue={getConvertedValue(props.rowData.priceUom, props.rowData.currencyCode, props.rowData.plattsPrice)}
              editor={(props) => (
                <MaskedNumber
                  value={props.value}
                  decimalScale={3}
                  fixedDecimalScale={true}
                  onChange={props.onChange}
                  style={{ width:"100%", fontSize: "0.875rem", textAlign: "right"}}
                />
              )}
              render={(props) => (
                <ConvertedNumber
                  displayType="text"
                  value={props.conversionValue}
                  convertedValue={props.value}
                  dontConvert={props.canEdit}
                  showError={false}
                  decimalScale={3}
                  rateProviderCode={props.rowData.exchangeRateType}
                  fromCurrencyCode={props.rowData.currencyCode}
                  toCurrencyCode="USDC"
                  rateDate={props.rowData.validFrom}
                  prefix="$c "
                  fixedDecimalScale={true}
                  onChange={props.onRenderChange}
                  uom={props.uom}
                  style={{ width:"100%", fontSize: "0.875rem", textAlign: "right"}}
                />
              )}
              mask="-9999999"
              maskChar={""}
              alwaysShowMask={false}
              formatChars={{ "9": "[0-9.]", "-": "[0-9.-]" }}
              //inputProps={{ style: { textAlign: "right", fontSize: "0.875rem" } }}
              rowData={props.rowData}
              onChange={(changedState) => {
                props.onRowDataChange({ ...props.rowData, overrideCalculation: changedState.canEdit, dollarPlattsPrice: changedState.value });
              }}
            />
          ),
          render: (rowData, rowType, updatedData) => {
            if (updatedData) {
              return (
                <ConvertedNumber
                  displayType="text"
                  value={updatedData.overrideCalculation ? updatedData.dollarPlattsPrice : updatedData.plattsPrice}
                  convertedValue={updatedData.dollarPlattsPrice}
                  dontConvert={updatedData.overrideCalculation}
                  decimalScale={3}
                  rateProviderCode={updatedData.exchangeRateType}
                  fromCurrencyCode={updatedData.currencyCode}
                  toCurrencyCode="USDC"
                  rateDate={updatedData.validFrom}
                  prefix="$c "
                  fixedDecimalScale={true}
                  uom={updatedData.priceUom}
                  onChange={(convertedValue) => {
                    if (tableRef && tableRef.current && tableRef.current.dataManager.lastEditingRow) {
                      if (tableRef.current.dataManager.lastEditingRow.fuelRateUuid === updatedData.fuelRateUuid) {
                        updatedData.dollarPlattsPrice = convertedValue
                      }
                    }
                    if (tableRef && tableRef.current && tableRef.current.state.showAddRow && rowType === "group") {
                      updatedData.dollarPlattsPrice = convertedValue;
                    }
                  }}
                  style={{textAlign: "right", color: updatedData.overrideCalculation?"#ff1A1A":undefined}}
                />);
            } else
              return 0.00;
          },
        },
        {
          title: (<span>Diffs<br/>($c/AG)</span>),//"Diffs ($c/AG)",
          field: "totalDiffs",
          sorting: false,
          type: "numeric",
          editable: "never",
          hidden: !state.canEdit,
          ...(ColumnWidth(85)),
          render: (rowData)=> {
            return (
              <FeeTooltip groupType="DIFFERENTIAL" fuelRateUuid={rowData.fuelRateUuid} placement="top-start">
                <div>
                  <MaskedNumber
                    displayType="text"
                    value={rowData && rowData.totalDiffs !== 0? rowData.totalDiffs : undefined}
                    decimalScale={3}
                    fixedDecimalScale={true}
                  />                  
                </div>
              </FeeTooltip>
            );
          }
        },
        {
          title: (<span>Fees<br/>($c/AG)</span>),//"Fees ($c/AG)",
          field: "totalFees",
          sorting: false,
          type: "numeric",
          editable: "never",
          hidden: !state.canEdit,
          ...(ColumnWidth(85)),
          render: (rowData)=> {
            return (
              <FeeTooltip groupType="FEE" fuelRateUuid={rowData.fuelRateUuid} placement="top-start">
                <div>
                  <MaskedNumber
                    displayType="text"
                    value={rowData && rowData.totalFees !== 0? rowData.totalFees : undefined}
                    decimalScale={3}
                    fixedDecimalScale={true}
                  />                  
                </div>
              </FeeTooltip>
            );
          }
        },
        {
          title: state.canEdit?(<span>Rate<br/>($c/AG)</span>):(<span>Rate<br/>($/AG)</span>),//"Rate ($c/AG)",
          field: "rate",
          sorting: false,
          type: "numeric",
          editable: "never",
          emptyValue: 0,
          initialEditValue: 0,
          ...(ColumnWidth(95)),
          render: (rowData, rowType, updatedData) => {
            const displayFactor = state.canEdit ? 1 : 0.01;
            const precision = state.canEdit ? 1000 : 1000000;
            const totalFees = isNaN(parseFloat(updatedData.totalFees)) ? 0 : parseFloat(updatedData.totalFees);
            const totalDiffs = isNaN(parseFloat(updatedData.totalDiffs)) ? 0 : parseFloat(updatedData.totalDiffs);
            const dollarPlattsPrice = isNaN(parseFloat(updatedData.dollarPlattsPrice)) ? 0 : parseFloat(updatedData.dollarPlattsPrice);
            updatedData.rate = Math.round((totalFees + totalDiffs + dollarPlattsPrice) * precision * displayFactor) / precision;
            if (isNaN(updatedData.rate)) {
              updatedData.rate = 0;
            } 
            return (
              <MaskedNumber
                style={{fontWeight: "bold"}}
                displayType="text"
                value={updatedData.rate}
                decimalScale={state.canEdit ? 3 : 5}
                fixedDecimalScale={true}
              />                  
            )
          },
        },
        {
          title: (<span>Fixed<br/>($/AG)</span>),//"Fixed ($/AG)",
          field: "totalFixedFees",
          sorting: false,
          type: "numeric",
          editable: "never",
          hidden: !state.canEdit && !state.canViewDetails,
          ...(ColumnWidth(85, undefined, undefined, {padding: "8px 10px 8px 2px" }, {padding: "8px 10px 8px 2px" })),
          render: (rowData)=> {
            return (
              <FeeTooltip groupType="FIXED" fuelRateUuid={rowData.fuelRateUuid} placement="top-start">
                <div>
                  <MaskedNumber
                    displayType="text"
                    prefix="$ "
                    value={rowData && rowData.totalFixedFees !== 0? rowData.totalFixedFees/100 : undefined}
                    decimalScale={3}
                    fixedDecimalScale={true}
                  />                  
                </div>
              </FeeTooltip>
            );
          }
        },
      ];
    },
    [state.canEdit, tableRef, today, validatorColumn]
  );

  const detailsChangeHandler = (changedFields, fuelRatesData) => {
    queryClient.setQueryData(['FuelRates', year, month], (records) => {
      return records.map(fuelRateRecord => {
        if (fuelRateRecord.fuelRateUuid === fuelRatesData.fuelRateUuid) {
          return { ...fuelRateRecord, ...changedFields };
        }
        return fuelRateRecord;
      });
    });
  }

  return (
    <div>
      <MaterialTable
        tableRef={tableRef}
        style={{
          border: "none",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          actionsColumnIndex: -1,
          rowStyle: (rowData)=> ({ backgroundColor: isNaN(parseFloat(rowData.plattsPrice))||parseFloat(rowData.plattsPrice)===0?"#f0f000":rowData.verifiedOn===null?rowData.tableData.checked?"#e0e000":"#ffff00":rowData.tableData.checked?"#e0e0e0":"#ffffff"}),
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle:{
            backgroundColor: theme.palette.tableHeader.backgroundColor,
            color: theme.palette.tableHeader.textColor,
            padding: "2px",
          },   
//          padding: 'dense',   
          toolbarButtonAlignment: "right",
          pageSize: rowsPerPage,
          detailPanelType: "single",
          selection: state.canEdit,
          selectionProps: rowData => ({
            disabled: !rowData.verifiedOn || (isNaN(parseFloat(rowData.plattsPrice))||parseFloat(rowData.plattsPrice)===0),
          })        
        }}
        actions={[
          {
            hidden: false,
            icon: 'save_alt',
            iconProps: { style: { color: theme.palette.primary.main } },
            tooltip: 'Export to Excel',
            isFreeAction: true,
            onClick: () => { downloadFuelRates(year, month, !state.canEdit) }
          },
          {
            position: 'row',
            action: rowData => ({
              hidden: !state.canEdit,
              icon: rowData.verifiedOn === null?'done':'done_all',
              tooltip: 'Confirm Rate and Fees are Correct',
              onClick: handleConfirm,
              disabled: (isNaN(parseFloat(rowData.plattsPrice))||parseFloat(rowData.plattsPrice)===0 || rowData.verifiedOn || !state.canEdit)
            })
          },
          {
            position: 'row',
            action: rowData => ({
              hidden: !state.canEdit,
              icon: 'file_copy',
              tooltip: 'Duplicate',
              position: 'row',
              onClick: handleDuplicate,
              disabled: !rowData.verifiedOn || !state.canEdit
            })
          },
          {
            hidden: !state.canEdit,
            icon: 'file_copy',
            tooltip: 'Duplicate Rows',
            onClick: handleDuplicateSelection
          },
        ]}
        components={{
          ...defaultComponents,
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[10, 15, 50]}
            />
          ),
        }}
        title="Fuel Rates"
        columns={getFuelColumns()}
        data={state.canEdit?monthlyFuelData.data:__.filter(monthlyFuelData.data,(rowData)=>(rowData.verifiedOn != null))}
        detailPanel={state.canEdit || state.canViewDetails? 
          [
            {
              tooltip: "Show Fuel Details",
              render: (rowData) => {
                return (
                  <div>
                    <div style={{ backgroundColor: "#f2f2f2" }}>
                      <FuelDetails
                        editFuelRoles={state.canEdit}
                        record={rowData}
                        onChange={(changedData) => {detailsChangeHandler(changedData, rowData, !state.canEdit)}}
                      />
                    </div>
                  </div>
                );
              },
            },
          ]:undefined
        }
        editable={{
          isEditable: (rowData) => {
            return rowData.priceBasis === "" && state.canEdit;
          },
          isDeletable: () => {
            return state.canEdit;
          },
          onRowAdd: state.canEdit
              ? (newData) => new Promise((resolve, reject) => {
                handleAddFuelRate.mutate({ record: newData, resolve, reject, extraKeys });
              })
              : undefined,
          onRowUpdate: state.canEdit
              ? (newData, oldData) =>
              new Promise((resolve, reject) => {
                handleUpdateFuelRate.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys });
              })
              :undefined,
          onRowDelete: state.canEdit
              ? (oldData) =>
              new Promise((resolve, reject) => {
                handleDeleteFuelRate.mutate({oldRecord: oldData, resolve, reject, extraKeys });
              })
              :undefined,

        }}
      />
      <FuelRateDuplicateDialog
        {...fuelRateDuplicateDialogProps}
      />
      {confirmFuelDialogProps.open ?
        <ConfirmFuelDialog
          {...confirmFuelDialogProps}
        />
        :<></>
      }
    </div>
  );
}

export default FuelRates;